// src/pages/Login.jsx
import { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import axios from "axios";
import config from "../config/env";
import { SESSION_NAME, useSessionContext } from "../contexts/session-context";
import { ReactComponent as LogoIcon } from "../assets/images/logo2.svg";

export default function Login() {
    // ✅ input เดียว: username หรือ phone
    const [loginId, setLoginId] = useState("");
    const [pwd, setPwd] = useState("");

    const [loading, setLoading] = useState(false);
    const [errMsg, setErrMsg] = useState("");

    // ✅ เพิ่ม: error ราย field (เพื่อทำขอบแดงเหมือนรูป)
    const [fieldErr, setFieldErr] = useState({ loginId: "", pwd: "" });

    const nav = useNavigate();
    const location = useLocation();
    const { refreshProfile } = useSessionContext();

    function extractToken(res) {
        const token = res?.data?.token || res?.data?.access_token || res?.data?.data?.token;
        const tokenValue = typeof token === "string" ? token : token?.token;
        return tokenValue || "";
    }

    async function loginWith(payload) {
        return axios.post(`${config.api || ""}/api/auth/login`, payload, {
            withCredentials: false,
        });
    }

    const onSubmit = async (e) => {
        e.preventDefault();
        if (loading) return;

        setLoading(true);
        setErrMsg("");
        setFieldErr({ loginId: "", pwd: "" }); // ✅ reset ก่อนลอง

        const value = String(loginId || "").trim();
        const password = String(pwd || "");

        const attempts = [{ username: value, password }, { phone_number: value, password }];
        let lastErr = null;

        try {
            let res = null;

            for (const payload of attempts) {
                try {
                    res = await loginWith(payload);
                    break;
                } catch (err) {
                    lastErr = err;
                }
            }

            if (!res) {
                const status = lastErr?.response?.status;

                // ✅ ทำขอบแดง + ข้อความใต้ช่องเหมือนรูป
                if (status === 401 || status === 400) {
                    setFieldErr({
                        loginId: "username หรือ หมายเลขโทรศัพท์ไม่ถูกต้อง",
                        pwd: "รหัสผ่านไม่ถูกต้อง กรุณากรอกใหม่อีกครั้ง",
                    });
                    setErrMsg(""); // ไม่จำเป็นต้องโชว์รวมแล้ว
                } else {
                    setErrMsg("ไม่สามารถเข้าสู่ระบบได้ กรุณาลองใหม่");
                    setFieldErr({
                        loginId: "ไม่สามารถเข้าสู่ระบบได้",
                        pwd: "ไม่สามารถเข้าสู่ระบบได้",
                    });
                }
                return;
            }

            const tokenValue = extractToken(res);
            if (!tokenValue) throw new Error("ไม่พบ token ในผลลัพธ์");

            localStorage.setItem(SESSION_NAME, tokenValue);
            localStorage.setItem("token", tokenValue);

            const user = res?.data?.user || null;
            if (user?.id) {
                localStorage.setItem("USER_ID", String(user.id));
                localStorage.setItem(
                    "profile",
                    JSON.stringify({
                        status: true,
                        token: tokenValue,
                        user,
                        message: res?.data?.message || "",
                    })
                );
            } else {
                localStorage.removeItem("USER_ID");
                localStorage.removeItem("profile");
            }

            await refreshProfile();

            const to = location.state?.from?.pathname || "/dashboard";
            nav(to, { replace: true });
        } catch (err) {
            console.error("login error:", err);
            setErrMsg("ไม่สามารถเข้าสู่ระบบได้ กรุณาลองใหม่");
            setFieldErr({
                loginId: "ไม่สามารถเข้าสู่ระบบได้",
                pwd: "ไม่สามารถเข้าสู่ระบบได้",
            });
        } finally {
            setLoading(false);
        }
    };

    const loginIdError = !!fieldErr.loginId;
    const pwdError = !!fieldErr.pwd;

    return (
        <div className="min-h-screen w-full bg-[#3F775F] flex items-center justify-center p-6">
            <div className="w-full max-w-[420px] rounded-3xl bg-white shadow-xl px-7 py-10">
                <div className="flex flex-col items-center">
                    <div className="text-center leading-none">
                        <LogoIcon className="mx-auto mb-2 h-16 w-auto" />
                    </div>
                    <div className="mt-2 text-[14px] text-[#6B7280]">
                        ลงชื่อเข้าใช้งานระบบ
                    </div>
                </div>

                <form onSubmit={onSubmit} className="mt-8 space-y-4">
                    <div>
                        <input
                            type="text"
                            placeholder="username หรือ หมายเลขโทรศัพท์"
                            className={[
                                "w-full h-12 rounded-lg border px-4 text-[15px] outline-none focus:ring-2",
                                loginIdError
                                    ? "border-red-500 text-red-500 focus:ring-red-500/20"
                                    : "border-[#CBD5E1] text-[#111827] focus:ring-[#3F775F]/20",
                            ].join(" ")}
                            value={loginId}
                            onChange={(e) => {
                                setLoginId(e.target.value);
                                // ✅ พิมพ์แล้วล้าง error ช่องนี้
                                if (fieldErr.loginId) setFieldErr((p) => ({ ...p, loginId: "" }));
                                if (errMsg) setErrMsg("");
                            }}
                            required
                            autoComplete="username"
                        />
                        {loginIdError && <div className="mt-1 text-[12px] text-red-500">{fieldErr.loginId}</div>}
                    </div>

                    <div>
                        <input
                            type="password"
                            placeholder="รหัสผ่าน"
                            className={[
                                "w-full h-12 rounded-lg border px-4 pr-4 text-[15px] outline-none focus:ring-2",
                                pwdError
                                    ? "border-red-500 text-red-500 focus:ring-red-500/20"
                                    : "border-[#CBD5E1] text-[#111827] focus:ring-[#3F775F]/20",
                            ].join(" ")}
                            value={pwd}
                            onChange={(e) => {
                                setPwd(e.target.value);
                                if (fieldErr.pwd) setFieldErr((p) => ({ ...p, pwd: "" }));
                                if (errMsg) setErrMsg("");
                            }}
                            required
                            autoComplete="current-password"
                        />
                        {pwdError && <div className="mt-1 text-[12px] text-red-500">{fieldErr.pwd}</div>}
                    </div>

                    <div className="flex items-center justify-end">
                        <Link
                            to="/forgot_password"
                            className="text-[13px] font-medium text-[#ED9C50] hover:underline"
                        >
                            ลืมรหัสผ่าน?
                        </Link>
                    </div>

                    {errMsg && <div className="text-sm text-rose-600">{errMsg}</div>}

                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full h-12 rounded-lg bg-[#3F775F] text-white text-[18px] font-bold disabled:opacity-60"
                    >
                        {loading ? "กำลังเข้าสู่ระบบ..." : "เข้าสู่ระบบ"}
                    </button>

                    <div className="pt-4 text-center text-[14px] text-[#6B7280]">
                        ไม่ได้เป็นบัญชีผู้ใช้งาน?{" "}
                        <Link
                            to="/register"
                            className="font-medium text-[#0F8A5F] hover:underline"
                        >
                            กรุณาลงทะเบียน
                        </Link>
                    </div>

                    {/* <div className="mt-6 text-center text-[12px] text-[#A1A1AA]">
                            © 2024 Thai Food Rescue. All rights reserved.
                        </div> */}
                    <div className="mt-2 text-center text-[12px] text-[#A1A1AA]">
                        version 1.0.0
                    </div>
                </form>
            </div>
        </div>
    );
}
